/**
 * 模型映射服务
 * 负责模型标识符与翻译提供商之间的映射和转换
 *
 * 核心功能：
 * 1. 将模型ID映射到对应的翻译提供商枚举
 * 2. 将翻译提供商枚举映射回模型ID
 * 3. 提供简化的获取模型提供商方法
 * 4. 支持日志记录，方便调试和追踪映射过程
 * 5. 管理模型支持的语言配置
 */
import { TranslationProvider } from '@readsavor/types'; // Removed unused TranslationModel
import { ModelLanguageRegistry } from '../../../language/modelLanguage/index.js';
import { SUPPORTED_LANGUAGES } from '../../../language/constants/language.config.js';
// 从单一数据源生成所有支持的语言代码列表
const ALL_SUPPORTED_LANGUAGES_CODES = SUPPORTED_LANGUAGES.map(lang => lang.code);
/**
 * 可用的模型列表
 */
export const AVAILABLE_MODELS = {
    'deepseek-r1': {
        id: 'deepseek-r1',
        name: 'DeepSeek V3.2 (Reasoner)',
        description: 'Advanced multilingual translation model for high-quality translation and analysis.',
        provider: TranslationProvider.DEEPSEEK,
    },
    'deepseek-v3': {
        id: 'deepseek-v3',
        name: 'DeepSeek V3.2',
        description: 'The latest DeepSeek large language model with enhanced contextual understanding.',
        provider: TranslationProvider.DEEPSEEK_V3,
    },
    'gemini-2.5-flash-lite': {
        id: 'gemini-2.5-flash-lite',
        name: 'Gemini 2.5 Flash Lite',
        description: "Google's latest lightweight model, fast and cost-effective.",
        provider: TranslationProvider.GEMINI_2_5_FLASH_LITE,
    },
    'gemini-2.5-flash': {
        id: 'gemini-2.5-flash',
        name: 'Gemini 2.5 Flash',
        description: "Google's latest generation speed-optimized model.",
        provider: TranslationProvider.GEMINI_2_5_FLASH,
    },
    'gemini-2.5-pro': {
        id: 'gemini-2.5-pro',
        name: 'Gemini 2.5 Pro',
        description: "Google's latest and most powerful flagship model.",
        provider: TranslationProvider.GEMINI_2_5_PRO,
    },
};
/**
 * 定义各个模型支持的语言配置
 * 目标语言列表现在由 language.config.ts 统一管理
 */
const MODEL_LANGUAGE_CONFIG = {
    'deepseek-r1': {
        sourceLanguages: ['auto'],
        targetLanguages: ALL_SUPPORTED_LANGUAGES_CODES,
        defaultSourceLanguage: 'auto',
        defaultTargetLanguage: 'en',
    },
    'deepseek-v3': {
        sourceLanguages: ['auto'],
        targetLanguages: ALL_SUPPORTED_LANGUAGES_CODES,
        defaultSourceLanguage: 'auto',
        defaultTargetLanguage: 'en',
    },
    'gemini-2.5-flash-lite': {
        sourceLanguages: ['auto'],
        targetLanguages: ALL_SUPPORTED_LANGUAGES_CODES,
        defaultSourceLanguage: 'auto',
        defaultTargetLanguage: 'en',
    },
    'gemini-2.5-flash': {
        sourceLanguages: ['auto'],
        targetLanguages: ALL_SUPPORTED_LANGUAGES_CODES,
        defaultSourceLanguage: 'auto',
        defaultTargetLanguage: 'en',
    },
    'gemini-2.5-pro': {
        sourceLanguages: ['auto'],
        targetLanguages: ALL_SUPPORTED_LANGUAGES_CODES,
        defaultSourceLanguage: 'auto',
        defaultTargetLanguage: 'en',
    },
};
/**
 * 模型映射服务
 * 提供模型ID与翻译提供商枚举之间的映射功能
 */
export class ModelMapperService {
    /**
     * 将模型ID映射到翻译提供商枚举
     * @param modelId 模型ID
     * @returns 对应的翻译提供商枚举值，如果找不到则返回默认值
     */
    static mapModelToProvider(modelId) {
        switch (modelId) {
            case 'deepseek-r1':
                return TranslationProvider.DEEPSEEK;
            case 'deepseek-v3':
                return TranslationProvider.DEEPSEEK_V3;
            case 'gemini-2.5-flash-lite':
                return TranslationProvider.GEMINI_2_5_FLASH_LITE;
            case 'gemini-2.5-flash':
                return TranslationProvider.GEMINI_2_5_FLASH;
            case 'gemini-2.5-pro':
                return TranslationProvider.GEMINI_2_5_PRO;
            default:
                // Instead of falling back, throw an error to make unsupported models explicit.
                throw new Error(`Unsupported modelId: ${modelId}`);
        }
    }
    /**
     * 将翻译提供商枚举映射到模型ID
     * @param provider 翻译提供商枚举
     * @returns 对应的模型ID，如果找不到则返回默认值
     */
    static mapProviderToModel(provider) {
        switch (provider) {
            case TranslationProvider.DEEPSEEK:
                return 'deepseek-r1';
            case TranslationProvider.DEEPSEEK_V3:
                return 'deepseek-v3';
            case TranslationProvider.GEMINI_2_5_FLASH_LITE:
                return 'gemini-2.5-flash-lite';
            case TranslationProvider.GEMINI_2_5_FLASH:
                return 'gemini-2.5-flash';
            case TranslationProvider.GEMINI_2_5_PRO:
                return 'gemini-2.5-pro';
            default:
                // Instead of falling back, throw an error to make unsupported providers explicit.
                throw new Error(`Unsupported provider: ${provider}`);
        }
    }
    /**
     * 获取指定模型ID对应的翻译提供商，并可选地记录日志
     * @param modelId 模型ID
     * @param enableLogging 是否启用日志记录
     * @returns 对应的翻译提供商枚举值
     */
    static getModelProvider(modelId, enableLogging = false) {
        const provider = this.mapModelToProvider(modelId);
        if (enableLogging) {
            // Logging is handled by the interceptor now.
        }
        return provider;
    }
    /**
     * 获取模型支持的语言配置
     * @param modelId 模型ID
     * @returns 模型语言支持配置
     */
    static getModelLanguageSupport(modelId) {
        return MODEL_LANGUAGE_CONFIG[modelId] || MODEL_LANGUAGE_CONFIG['deepseek-v3'];
    }
}
/**
 * 初始化模型语言注册表
 */
export function initModelLanguageRegistry() {
    const registry = ModelLanguageRegistry.getInstance();
    // 注册所有模型的语言支持配置
    Object.entries(AVAILABLE_MODELS).forEach(([key, _model]) => {
        const config = MODEL_LANGUAGE_CONFIG[key] || MODEL_LANGUAGE_CONFIG['deepseek-v3'];
        registry.registerModel(key, config);
    });
}
// 执行初始化
initModelLanguageRegistry();
//# sourceMappingURL=modelMapperService.js.map